import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CreditCardOutlined,
  WorkspacePremiumOutlined,
  TokenOutlined,
  AllInclusiveOutlined,
  CheckCircleOutlined,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { UserContext } from "../../context/UserContext";
import { formatPlanPriceInDisplay, resolvePlanDisplayCurrency } from "../../utils/planDisplayCurrency";
import TransferCreditsPanel from "./TransferCreditsPanel";
import PromoRedeemPanel from "./PromoRedeemPanel";
function planField(p, snake, camel) {
  const v = p?.[snake] ?? p?.[camel];
  return v != null && v !== "" ? v : null;
}

function isTrialOrFreePlan(plan) {
  const raw = planField(plan, "price", "price");
  const n = parseFloat(String(raw ?? "0"));
  return !Number.isFinite(n) || n <= 0;
}

function getPaystackCtor() {
  if (typeof window !== "undefined") {
    if (typeof window.PaystackPop === "function") return window.PaystackPop;
    // v2 CDN sometimes sets it as an object with a default export pattern
    if (window.PaystackPop && typeof window.PaystackPop.default === "function")
      return window.PaystackPop.default;
  }
  return null;
}

function paystackErrDetail(err) {
  if (err == null) return "";
  if (typeof err === "string") return err;
  if (typeof err.message === "string" && err.message.trim()) return err.message.trim();
  try {
    return JSON.stringify(err);
  } catch {
    return "";
  }
}

const Billing = ({ lang }) => {
  const theme = useTheme();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const { userData, getUserData } = React.useContext(UserContext);

  const [plans, setPlans] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [planDisplay, setPlanDisplay] = React.useState(() => resolvePlanDisplayCurrency({}));
  const [paystackGwData, setPaystackGwData] = React.useState(null);
  const [paying, setPaying] = React.useState("");
  const [paymentResult, setPaymentResult] = React.useState(null); // { success, msg }

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [pRes, gwRes] = await Promise.all([
          hitAxios({ path: "/api/plan/get_all", post: false, admin: false, showLoading: false }),
          hitAxios({ path: "/api/payment/active-gateways", post: false, admin: false, showLoading: false }),
        ]);
        if (!cancelled && pRes?.data?.success) {
          const raw = Array.isArray(pRes.data.plans) ? pRes.data.plans : [];
          setPlans(raw.filter((p) => !isTrialOrFreePlan(p)));
        }
        if (!cancelled && gwRes?.data?.success) {
          const gwData = gwRes.data.data;
          setPlanDisplay(resolvePlanDisplayCurrency(gwData));
          setPaystackGwData(gwData?.paystack ?? null);
        } else if (!cancelled) {
          setPlanDisplay(resolvePlanDisplayCurrency({}));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hitAxios]);

  const credits = Number(userData?.credits ?? 0);
  const planJson = userData?.plan;
  let currentPlan = null;
  try {
    currentPlan =
      typeof planJson === "string" && planJson
        ? JSON.parse(planJson)
        : planJson && typeof planJson === "object"
          ? planJson
          : null;
  } catch {
    currentPlan = null;
  }

  const noCredits = credits <= 0;

  async function handlePaystack(plan) {
    const planId = plan.id;
    setPaying(String(planId));
    try {
      const res = await hitAxios({
        path: "/api/payment/paystack/create-order",
        admin: false,
        post: true,
        obj: { plan_id: planId },
        showLoading: false,
      });
      if (!res?.data?.success) {
        setPaying("");
        return;
      }

      let d = res.data || {};
      if (d.success && d.data && typeof d.data === "object" && !Array.isArray(d.data)) {
        d = { ...d, ...d.data };
      }

      let PaystackCtor = getPaystackCtor();
      if (!PaystackCtor) {
        try {
          const mod = await import("@paystack/inline-js");
          PaystackCtor =
            typeof mod?.default === "function"
              ? mod.default
              : typeof mod?.PaystackPop === "function"
                ? mod.PaystackPop
                : typeof mod === "function"
                  ? mod
                  : null;
        } catch (importErr) {
          PaystackCtor = null;
        }
      }
      if (typeof PaystackCtor !== "function") {
        setPaying("");
        showSnack(lang?.paystackInlineError || "Payment could not open. Please try again.", false);
        return;
      }

      const accessCode = d.access_code ?? d.accessCode;
      const publicKey = d.publicKey ?? d.public_key ?? paystackGwData?.publicKey;

      // Fallback to logged-in user's email when backend omits it
      const email =
        (typeof d.email === "string" && d.email.trim()) ||
        (typeof userData?.email === "string" && userData.email.trim()) ||
        "";

      // Fallback: compute amount from plan price × settlement rate when backend omits it
      const settlementRate = Number(paystackGwData?.settlement?.rate) > 0
        ? Number(paystackGwData.settlement.rate)
        : 1;
      const rawAmount = Number(d.amount);
      const amountNum = Number.isFinite(rawAmount) && rawAmount > 0
        ? rawAmount
        : Math.round(parseFloat(String(plan.price ?? "0")) * settlementRate * 100);
      const amountOk = Number.isFinite(amountNum) && amountNum > 0;

      const onSuccess = (transaction) => {
        const ref = transaction?.reference || transaction?.trxref;
        if (!ref) {
          setPaying("");
          return;
        }
        try {
          sessionStorage.setItem("checkoutReturn", "/user?page=billing");
        } catch {
          /* non-fatal */
        }
        window.location.assign(
          `/checkout/thank-you?gateway=paystack&reference=${encodeURIComponent(String(ref))}`,
        );
      };

      const onCancel = () => setPaying("");

      const onError = (err) => {
        setPaying("");
        const detail = paystackErrDetail(err);
        showSnack(detail || lang?.paystackInlineError || "Payment could not open. Please try again.", false);
      };

      let popup;
      try {
        popup = new PaystackCtor();
      } catch (e) {
        setPaying("");
        showSnack(e?.message || lang?.paystackInlineError || "Payment could not open.", false);
        return;
      }

      if (accessCode) {
        popup.resumeTransaction(String(accessCode), { onSuccess, onCancel, onError });
        return;
      }

      if (!publicKey || !email || !amountOk) {
        setPaying("");
        showSnack(lang?.paystackInlineError || "Payment could not open. Please try again.", false);
        return;
      }

      const currency =
        d.currency ||
        paystackGwData?.settlement?.code ||
        "NGN";

      // metadata.uid + metadata.plan_id are required by the verify endpoint
      const metadata = d.metadata || {
        plan_id: String(plan.id),
        uid: String(userData?.uid || ""),
        plan_title: plan.title || "",
      };

      const txParams = {
        key: publicKey,
        email,
        amount: amountNum,
        currency,
        metadata,
        onSuccess,
        onCancel,
        onError,
      };
      if (d.reference) txParams.reference = d.reference;

      popup.newTransaction(txParams);
    } catch (e) {
      setPaying("");
      showSnack(e?.message || lang?.paystackInlineError || "Payment could not open. Please try again.", false);
    }
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <CreditCardOutlined color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={800}>
            {lang?.billingTitle || "Billing"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {lang?.billingSubtitle ||
              "Choose a paid plan to activate your account and add credits."}
          </Typography>
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
            {planDisplay.isSinglePaystackNgn
              ? lang?.billingPlansNgnNote ||
                "Prices are in Nigerian Naira (Paystack NGN is your only card payment method)."
              : String(planDisplay.currency?.code || "").toUpperCase() === "NGN"
                ? lang?.billingPlansNgnMultiNote ||
                  "Prices are shown in Nigerian Naira (Paystack NGN). Paying with another card method may settle in USD at that provider's rate."
                : lang?.billingPlansUsdNote ||
                  "Plan prices are shown in USD when Paystack NGN is not used for display, or the active gateway settles in USD."}
          </Typography>
        </Box>
      </Stack>

      {paymentResult && (
        <Alert
          severity={paymentResult.success ? "success" : "error"}
          icon={paymentResult.success ? <CheckCircleOutlined fontSize="inherit" /> : undefined}
          onClose={() => setPaymentResult(null)}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {paymentResult.msg}
        </Alert>
      )}

      {!paymentResult && noCredits && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {lang?.billingNoCredits ||
            "You have no credits left. Buy more credits below to keep using paid features."}
        </Alert>
      )}

      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.06 : 0.04),
        }}
      >
        <CardContent sx={{ py: 2.5, px: { xs: 2.5, sm: 3 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                icon={<TokenOutlined sx={{ fontSize: "16px !important" }} />}
                label={`${credits.toLocaleString()} ${lang?.credits || "credits"}`}
                color={noCredits ? "error" : "primary"}
                variant={noCredits ? "filled" : "outlined"}
                sx={{ fontWeight: 800 }}
              />
              {currentPlan?.title && (
                <Chip
                  icon={<WorkspacePremiumOutlined sx={{ fontSize: "16px !important" }} />}
                  label={currentPlan.title}
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              )}
              {currentPlan && (
                <Chip
                  icon={<AllInclusiveOutlined sx={{ fontSize: "16px !important" }} />}
                  label={lang?.creditsNeverExpire || "Credits never expire"}
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>
            <Button
              variant="outlined"
              size="small"
              onClick={() => getUserData?.()}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              {lang?.billingRefresh || "Refresh balance"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <PromoRedeemPanel lang={lang} />
      <TransferCreditsPanel lang={lang} />

      <Stack direction="row" alignItems="baseline" justifyContent="space-between" mb={2} mt={0.5}>
        <Box>
          <Typography variant="h6" fontWeight={800} letterSpacing="-0.01em">
            {lang?.billingBuyTitle || "Buy credit packages"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {lang?.billingBuySubtitle || "Subscribe to add credits to your own account."}
          </Typography>
        </Box>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : plans.length === 0 ? (
        <Alert severity="info">
          {lang?.billingNoPaidPlans ||
            lang?.billingNoPlans ||
            "No paid plans are available right now."}
        </Alert>
      ) : (
        <Grid container spacing={2.5}>
          {plans.map((plan) => {
            const id = plan.id;
            const title = plan.title || `Plan ${id}`;
            const price = planField(plan, "price", "price");
            const strike = planField(plan, "price_strike", "priceStrike");
            const pcredits = planField(plan, "credits", "credits");
            const pmax = planField(plan, "max_characters", "maxCharacters");
            const popular = plan.popular === 1 || plan.popular === "1";
            const isPayingThis = paying === String(id);
            const isVerifying = paying === "verifying";

            return (
              <Grid item xs={12} sm={6} md={4} key={id}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: `1px solid ${
                      popular
                        ? alpha(theme.palette.primary.main, 0.5)
                        : theme.palette.divider
                    }`,
                    background: popular
                      ? `linear-gradient(160deg, ${alpha(
                          theme.palette.primary.main,
                          theme.palette.mode === "dark" ? 0.12 : 0.06,
                        )} 0%, ${theme.palette.background.paper} 55%)`
                      : theme.palette.background.paper,
                    transition: "transform 0.2s ease, border-color 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      borderColor: alpha(theme.palette.primary.main, 0.45),
                    },
                  }}
                >
                  <CardContent sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2.75 }}>
                    {popular && (
                      <Chip
                        size="small"
                        label={lang?.popular || "Popular"}
                        color="primary"
                        sx={{ alignSelf: "flex-start", mb: 1.25, fontWeight: 800 }}
                      />
                    )}
                    <Typography variant="h6" fontWeight={800} letterSpacing="-0.01em" gutterBottom>
                      {title}
                    </Typography>
                    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.25 }}>
                      <Typography variant="h4" fontWeight={800} color="primary.main">
                        {planDisplay.currency.symbol}
                        {formatPlanPriceInDisplay(price, planDisplay.currency)}
                      </Typography>
                      {strike && parseFloat(String(strike)) > parseFloat(String(price || "0")) && (
                        <Typography variant="body2" color="text.disabled" sx={{ textDecoration: "line-through" }}>
                          {planDisplay.currency.symbol}
                          {formatPlanPriceInDisplay(strike, planDisplay.currency)}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {planDisplay.currency.code}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, flexGrow: 1 }}>
                      {pcredits != null ? `${Number(pcredits).toLocaleString()} ${lang?.credits || "credits"}` : ""}
                      {pmax != null
                        ? ` · ${Number(pmax).toLocaleString()} ${lang?.pricing_aiModels || "AI models"}`
                        : ""}
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={!!paying || !id}
                      onClick={() => handlePaystack(plan)}
                      startIcon={(isPayingThis || isVerifying) ? <CircularProgress size={18} color="inherit" /> : null}
                      sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2, py: 1.15 }}
                    >
                      {isVerifying
                        ? lang?.verifyingPayment || "Verifying…"
                        : isPayingThis
                          ? lang?.paystackOpening || "Opening secure payment…"
                          : lang?.billingSubscribe || "Subscribe"}
                    </Button>
                    <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1.25}>
                      {lang?.billingCheckoutNote || "Payment opens in a secure overlay on this page."}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default Billing;
